import axios from 'axios';
import session from '@/resource/memberReviewSession';

// e.g. https://qa-api.purenroll.com/api.member/ + v2/member-review/
const BASE_URL = (process.env.VUE_APP_CORENROLL_MEMBER_API || '') + 'v2/member-review/';
const MOCK = process.env.VUE_APP_MEMBER_REVIEW_MOCK === 'true';

// Own instance: the global axios response interceptor redirects 401s to login.
const http = axios.create();

http.interceptors.request.use((config) => {
    const token = session.get();
    if (token) {
        config.headers['X-Review-Token'] = token;
    }
    const access = session.getAccess();
    if (access) {
        config.headers['X-Review-Access'] = access;
    }
    return config;
});

http.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response && error.response.status;
        const data = (error.response && error.response.data) || {};
        if (status === 401 && data.otp_required) {
            requireOtp();
        } else if (status === 401 || status === 403) {
            expire();
        }
        return Promise.reject(error);
    }
);

// Lazy require avoids a router <-> view <-> api import cycle.
function router() {
    return require('@/router').default;
}

export function expire() {
    session.clear();
    if (router().currentRoute.name !== 'MemberReviewExpired') {
        router().replace({ name: 'MemberReviewExpired' });
    }
}

// Link is still valid but the OTP access is missing or expired: back to the code screen.
export function requireOtp() {
    const token = session.get();
    session.clearAccess();
    if (!token) {
        expire();
        return;
    }
    if (router().currentRoute.name !== 'MemberReviewEntry') {
        router().replace({ name: 'MemberReviewEntry', params: { token } });
    }
}

function request(method, path, data) {
    if (MOCK) {
        return require('@/resource/memberReviewMock').default.handle(method, path, data);
    }
    // Reused rep endpoints answer {success: true, data} without a status field.
    return http.request({ method, url: BASE_URL + path, data }).then((response) => {
        if (response.data && response.data.status === 'error') {
            return Promise.reject(response);
        }
        return response;
    });
}

// OTP gate. These use the link token only (session.get()); verify returns the
// access token the other endpoints need. Wrong/expired code = 422, lockout or
// resend too soon = 429, link invalid = 401.
function getOtpStatus() {
    return request('get', 'otp');
}

function sendOtp() {
    return request('post', 'otp/send');
}

function verifyOtp(code) {
    return request('post', 'otp/verify', { otp: code }).then((response) => ({
        accessToken: response.data.data.access_token,
    }));
}

export default {
    getOtpStatus,
    sendOtp,
    verifyOtp,
    getStatus: () => request('get', 'status'),
    getTabs: () => request('get', 'tabs'),
    getSection: (section) => request('get', section),
    patchSection: (section, payload) => request('patch', section, payload),
    getPolicyTiers: () => request('get', 'policy-tiers'),
    getCart: () => request('get', 'cart'),
    getAgreements: () => request('get', 'agreements'),
    validateSingleDependent: (payload) => request('post', 'validate-single-dependent', payload),
    recordPregnancyRestriction: (payload) => request('post', 'pregnancy-restriction', payload),
    validateRoutingNumber: (routingNumber) => request('post', 'validate-routing-number', { routing_number: routingNumber }),
    submit: (payload) => request('post', 'submit', payload),
};
